import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TaxRateSet } from '@/api/models';
import { useTaxSettingsOnDateQuery } from '@/api/queries';
import { unwrapStatement } from '@/pages/accounting/statements/statementMeta';
import { todayIso } from '../../settingsMeta';

export type UseRatesOnDateResult = {
    /** The date being asked about. Empty until the user picks one. */
    date: string;
    onDateChange: (value: string) => void;
    isLoading: boolean;
    /** The set in force on that date, once resolved. */
    set: TaxRateSet | null;
    /**
     * The date is earlier than the ledger's earliest recorded set.
     *
     * This is an **answer**, not a failure: "nothing was in force yet" is a
     * true and useful thing to learn about a date before the ledger existed.
     * It is kept separate from `error` for exactly that reason.
     */
    isBeforeLedger: boolean;
    /** A genuine failure — network, auth, anything unexpected. */
    error: ErrorResponse | null;
    onRetry: () => void;
};

/**
 * "What rates did July's payroll use?"
 *
 * This is the surface that makes effective dating tangible. Everything else
 * records a timeline; this is the only place a user can interrogate it.
 */
export const useRatesOnDate = (): UseRatesOnDateResult => {
    const [date, setDate] = React.useState<string>(() => todayIso());

    const query = useTaxSettingsOnDateQuery(date || null);
    const { data, error } = unwrapStatement(query.data, query.error);

    // 400 NO_RATES_IN_FORCE_ON_DATE is the documented answer for a date that
    // predates the ledger. Treating it as an error would turn a reasonable
    // question into a red banner.
    const isBeforeLedger = error?.code === 'NO_RATES_IN_FORCE_ON_DATE';

    const onDateChange = React.useCallback((value: string) => {
        setDate(value);
    }, []);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    return {
        date,
        onDateChange,
        isLoading: query.isFetching,
        set: data ?? null,
        isBeforeLedger,
        error: isBeforeLedger ? null : (error ?? null),
        onRetry,
    };
};
